import { useState, useMemo } from 'react';
import { Download, FileText, Loader2, AlertCircle } from 'lucide-react';
import { useSites } from '../../hooks/useSites';
import { useSiteDevices } from '../../hooks/useSiteDevices';
import {
  runIntervalReport,
  runDailyReport,
  runMonthlyReport,
  downloadReportCsv,
  extractHistorianError,
  type HistorianErrorBody,
  type IntervalReportRow,
  type DailyReportRow,
  type MonthlyReportRow,
  type ReportResult,
} from '../../services/historianReportsApi';

/**
 * CannedReportsCard — djin-cloud#60
 *
 * Three canned historian reports (Interval / Daily / Monthly) with a native
 * slicer-style date/month picker and a Download CSV button. Mounted above the
 * existing query builder on the Historian page; reuses the page's
 * RoleGatedRoute RBAC envelope (VIEW_ANALYTICS).
 *
 * Design notes:
 *   - Native <input type="date"|"month"> keeps the dependency footprint zero.
 *     A richer slicer component is tracked in the follow-on slicer UX issue.
 *   - Consumption (kWh) and Demand (Peak kW) render side-by-side — both are
 *     derivable from the same power metric so a toggle would add friction
 *     rather than remove it.
 *   - Backend auto-resolves the power metric via
 *     DEFAULT_POWER_METRIC_PRIORITY (`grid_power_kw` →`active_power_kw` →
 *     `total_active_power`). When none is present the server returns
 *     400 unknown_power_metric with a hint listing observed metrics, which
 *     we surface in the error banner.
 *   - `timezone` comes from the Site record (IANA name). No client-side
 *     date math — we ship `YYYY-MM-DD` / `YYYY-MM` plus `tz` and let the
 *     server convert to UTC.
 */

type ReportTab = 'interval' | 'daily' | 'monthly';

const TAB_LABELS: Record<ReportTab, string> = {
  interval: '15-min Interval (day)',
  daily: 'Daily (month)',
  monthly: 'Monthly (range)',
};

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function yesterdayYmd(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function thisMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

function lastMonth(): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

function monthsAgo(n: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

function formatNumber(v: number | null | undefined, digits = 2): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return v.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatDay(iso: string): string {
  return iso.slice(0, 10);
}

function formatMonth(iso: string): string {
  return iso.slice(0, 7);
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function CannedReportsCard() {
  const [tab, setTab] = useState<ReportTab>('interval');

  const { data: sitesData } = useSites({ limit: 200 });
  const [siteId, setSiteId] = useState<string>('');
  const selectedSite = useMemo(
    () => sitesData?.sites.find(s => s.site_id === siteId),
    [sitesData, siteId],
  );
  const tz = selectedSite?.timezone || 'UTC';

  const { data: siteDevices } = useSiteDevices(siteId);
  const [deviceIds, setDeviceIds] = useState<string[]>([]);

  const [day, setDay] = useState<string>(yesterdayYmd());
  const [month, setMonth] = useState<string>(lastMonth());
  const [startMonth, setStartMonth] = useState<string>(monthsAgo(5));
  const [endMonth, setEndMonth] = useState<string>(thisMonth());

  const [intervalResult, setIntervalResult] = useState<ReportResult<IntervalReportRow> | null>(null);
  const [dailyResult, setDailyResult] = useState<ReportResult<DailyReportRow> | null>(null);
  const [monthlyResult, setMonthlyResult] = useState<ReportResult<MonthlyReportRow> | null>(null);

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [errorBody, setErrorBody] = useState<HistorianErrorBody | null>(null);

  const canRun = !!siteId && !!tz;

  const handleToggleDevice = (id: string) => {
    setDeviceIds(prev => (prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]));
  };

  const handleRun = async () => {
    if (!canRun) return;
    setLoading(true);
    setErrorBody(null);
    try {
      if (tab === 'interval') {
        const res = await runIntervalReport({
          siteId,
          deviceIds: deviceIds.length > 0 ? deviceIds : undefined,
          day,
          tz,
        });
        setIntervalResult(res);
      } else if (tab === 'daily') {
        const res = await runDailyReport({
          siteId,
          deviceIds: deviceIds.length > 0 ? deviceIds : undefined,
          month,
          tz,
        });
        setDailyResult(res);
      } else {
        const res = await runMonthlyReport({
          siteId,
          deviceIds: deviceIds.length > 0 ? deviceIds : undefined,
          startMonth,
          endMonth,
          tz,
        });
        setMonthlyResult(res);
      }
    } catch (err) {
      setErrorBody(extractHistorianError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!canRun) return;
    setDownloading(true);
    setErrorBody(null);
    try {
      if (tab === 'interval') {
        await downloadReportCsv(
          'interval',
          { siteId, deviceIds: deviceIds.length > 0 ? deviceIds : undefined, day, tz },
          `historian_interval_${siteId}_${day}.csv`,
        );
      } else if (tab === 'daily') {
        await downloadReportCsv(
          'daily',
          { siteId, deviceIds: deviceIds.length > 0 ? deviceIds : undefined, month, tz },
          `historian_daily_${siteId}_${month}.csv`,
        );
      } else {
        await downloadReportCsv(
          'monthly',
          { siteId, deviceIds: deviceIds.length > 0 ? deviceIds : undefined, startMonth, endMonth, tz },
          `historian_monthly_${siteId}_${startMonth}_to_${endMonth}.csv`,
        );
      }
    } catch (err) {
      setErrorBody(extractHistorianError(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-5 space-y-4">
      <div className="flex items-center gap-3">
        <FileText className="w-5 h-5 text-ems-green" />
        <div>
          <h2 className="text-base font-bold text-ems-text-primary">Reports</h2>
          <p className="text-xs text-ems-text-muted">
            Pick a site and time window, then Run Report or Download CSV.
          </p>
        </div>
      </div>

      {/* Tab strip */}
      <div className="flex gap-2 border-b border-ems-border">
        {(['interval', 'daily', 'monthly'] as const).map(t => (
          <button
            key={t}
            onClick={() => {
              setTab(t);
              // Clear the error banner on tab change — an error raised in one
              // tab's context (e.g. "CSV export requires export_data") is
              // misleading when shown on another tab. Successful result
              // previews are preserved per-tab so operators can flip between
              // runs without re-querying.
              setErrorBody(null);
            }}
            className={[
              'px-3 py-2 text-sm font-medium transition-colors rounded-t-md',
              tab === t
                ? 'text-ems-green border-b-2 border-ems-green -mb-px'
                : 'text-ems-text-secondary hover:text-ems-text-primary',
            ].join(' ')}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {/* Common controls — site + device picker */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-ems-text-muted uppercase mb-1.5">Site</label>
          <select
            value={siteId}
            onChange={e => {
              setSiteId(e.target.value);
              setDeviceIds([]);
            }}
            className="w-full text-sm border border-ems-border rounded-lg px-3 py-2 text-ems-text-primary bg-ems-bg focus:outline-none focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
          >
            <option value="">Select a site…</option>
            {sitesData?.sites.map(site => (
              <option key={site.site_id} value={site.site_id}>
                {site.name}
              </option>
            ))}
          </select>
          {selectedSite && (
            <p className="text-xs text-ems-text-muted mt-1 px-0.5">Timezone: {tz}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-ems-text-muted uppercase mb-1.5">
            Devices (optional)
          </label>
          <div className="border border-ems-border rounded-lg max-h-24 overflow-y-auto p-2 space-y-1">
            {siteId && siteDevices && siteDevices.length > 0 ? (
              siteDevices.map(d => (
                <label
                  key={d.device_id}
                  className="flex items-center gap-2 text-sm cursor-pointer hover:bg-ems-bg-card-hover px-1.5 py-1 rounded"
                >
                  <input
                    type="checkbox"
                    checked={deviceIds.includes(d.device_id)}
                    onChange={() => handleToggleDevice(d.device_id)}
                    className="rounded border-ems-border text-ems-green focus:ring-ems-green/20"
                  />
                  <span className="text-ems-text-primary truncate" title={d.device_id}>
                    {d.display_name || d.device_id}
                  </span>
                  <span className="text-xs text-ems-text-muted">{d.device_type}</span>
                </label>
              ))
            ) : (
              <p className="text-xs text-ems-text-muted px-1">
                {siteId ? 'No devices on this site.' : 'Select a site first.'}
              </p>
            )}
          </div>
          {deviceIds.length === 0 && siteId && (
            <p className="text-xs text-ems-text-muted mt-1 px-0.5">
              All devices (site-wide aggregate)
            </p>
          )}
        </div>
      </div>

      {/* Tab-specific slicer controls */}
      {tab === 'interval' && (
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-ems-text-muted uppercase mb-1.5">
            Day
          </label>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="date"
              value={day}
              onChange={e => setDay(e.target.value)}
              className="text-sm border border-ems-border rounded-lg px-3 py-2 text-ems-text-primary bg-ems-bg focus:outline-none focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
            />
            <button
              onClick={() => setDay(todayYmd())}
              className="px-2 py-1 text-xs rounded border border-ems-border text-ems-text-secondary hover:bg-ems-bg-card-hover"
            >
              Today
            </button>
            <button
              onClick={() => setDay(yesterdayYmd())}
              className="px-2 py-1 text-xs rounded border border-ems-border text-ems-text-secondary hover:bg-ems-bg-card-hover"
            >
              Yesterday
            </button>
          </div>
        </div>
      )}

      {tab === 'daily' && (
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-ems-text-muted uppercase mb-1.5">
            Month
          </label>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="month"
              value={month}
              onChange={e => setMonth(e.target.value)}
              className="text-sm border border-ems-border rounded-lg px-3 py-2 text-ems-text-primary bg-ems-bg focus:outline-none focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
            />
            <button
              onClick={() => setMonth(thisMonth())}
              className="px-2 py-1 text-xs rounded border border-ems-border text-ems-text-secondary hover:bg-ems-bg-card-hover"
            >
              This month
            </button>
            <button
              onClick={() => setMonth(lastMonth())}
              className="px-2 py-1 text-xs rounded border border-ems-border text-ems-text-secondary hover:bg-ems-bg-card-hover"
            >
              Last month
            </button>
          </div>
        </div>
      )}

      {tab === 'monthly' && (
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-ems-text-muted uppercase mb-1.5">
            Month Range
          </label>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="month"
              value={startMonth}
              onChange={e => setStartMonth(e.target.value)}
              className="text-sm border border-ems-border rounded-lg px-3 py-2 text-ems-text-primary bg-ems-bg focus:outline-none focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
            />
            <span className="text-xs text-ems-text-muted">to</span>
            <input
              type="month"
              value={endMonth}
              onChange={e => setEndMonth(e.target.value)}
              className="text-sm border border-ems-border rounded-lg px-3 py-2 text-ems-text-primary bg-ems-bg focus:outline-none focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
            />
            <button
              onClick={() => {
                setStartMonth(monthsAgo(2));
                setEndMonth(thisMonth());
              }}
              className="px-2 py-1 text-xs rounded border border-ems-border text-ems-text-secondary hover:bg-ems-bg-card-hover"
            >
              Last 3 months
            </button>
            <button
              onClick={() => {
                setStartMonth(monthsAgo(5));
                setEndMonth(thisMonth());
              }}
              className="px-2 py-1 text-xs rounded border border-ems-border text-ems-text-secondary hover:bg-ems-bg-card-hover"
            >
              Last 6 months
            </button>
            <button
              onClick={() => {
                setStartMonth(`${new Date().getFullYear()}-01`);
                setEndMonth(thisMonth());
              }}
              className="px-2 py-1 text-xs rounded border border-ems-border text-ems-text-secondary hover:bg-ems-bg-card-hover"
            >
              Year-to-date
            </button>
            <button
              onClick={() => {
                setStartMonth(monthsAgo(11));
                setEndMonth(thisMonth());
              }}
              className="px-2 py-1 text-xs rounded border border-ems-border text-ems-text-secondary hover:bg-ems-bg-card-hover"
            >
              Last 12 months
            </button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={handleRun}
          disabled={!canRun || loading}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-ems-green text-white hover:bg-ems-green-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Run Report
        </button>
        <button
          onClick={handleDownload}
          disabled={!canRun || downloading}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-ems-border text-ems-text-primary hover:bg-ems-bg-card-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download CSV
        </button>
      </div>

      {/* Error banner */}
      {errorBody && (
        <div className="flex items-start gap-2 p-3 rounded-lg border border-red-500/30 bg-red-500/5 text-sm">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-red-500 font-medium">{errorBody.message}</p>
            {errorBody.hint && <p className="text-ems-text-muted text-xs mt-1">{errorBody.hint}</p>}
          </div>
        </div>
      )}

      {/* Preview tables */}
      {tab === 'interval' && intervalResult && (
        <IntervalPreview result={intervalResult} />
      )}
      {tab === 'daily' && dailyResult && <DailyPreview result={dailyResult} />}
      {tab === 'monthly' && monthlyResult && <MonthlyPreview result={monthlyResult} />}
    </div>
  );
}

function IntervalPreview({ result }: { result: ReportResult<IntervalReportRow> }) {
  return (
    <PreviewFrame metadata={result.metadata}>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-ems-text-muted">
          <tr>
            <th className="text-left px-3 py-2">Time</th>
            <th className="text-right px-3 py-2">Peak kW</th>
            <th className="text-right px-3 py-2">Avg kW</th>
            <th className="text-right px-3 py-2">kWh</th>
            <th className="text-right px-3 py-2">kvar</th>
            <th className="text-right px-3 py-2">PF</th>
            <th className="text-right px-3 py-2">Fuel MMBtu/h</th>
            <th className="text-right px-3 py-2">Fuel MMBtu</th>
          </tr>
        </thead>
        <tbody>
          {result.rows.map(r => (
            <tr key={r.bucket_start} className="border-t border-ems-border">
              <td className="px-3 py-1.5 text-ems-text-primary">{formatTimestamp(r.bucket_start)}</td>
              <td className="px-3 py-1.5 text-right text-ems-text-primary">{formatNumber(r.peak_kw)}</td>
              <td className="px-3 py-1.5 text-right text-ems-text-primary">{formatNumber(r.avg_kw)}</td>
              <td className="px-3 py-1.5 text-right text-ems-text-primary">{formatNumber(r.kwh, 3)}</td>
              <td className="px-3 py-1.5 text-right text-ems-text-primary">{formatNumber(r.kvar)}</td>
              <td className="px-3 py-1.5 text-right text-ems-text-primary">{formatNumber(r.pf, 3)}</td>
              <td className="px-3 py-1.5 text-right text-ems-text-primary">{formatNumber(r.fuel_rate_mmbtu_h, 3)}</td>
              <td className="px-3 py-1.5 text-right text-ems-text-primary">{formatNumber(r.energy_mmbtu, 3)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </PreviewFrame>
  );
}

function DailyPreview({ result }: { result: ReportResult<DailyReportRow> }) {
  return (
    <PreviewFrame metadata={result.metadata}>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-ems-text-muted">
          <tr>
            <th className="text-left px-3 py-2">Day</th>
            <th className="text-right px-3 py-2">Daily Peak kW</th>
            <th className="text-right px-3 py-2">Daily kWh</th>
            <th className="text-right px-3 py-2">Daily MMBtu</th>
          </tr>
        </thead>
        <tbody>
          {result.rows.map(r => (
            <tr key={r.day} className="border-t border-ems-border">
              <td className="px-3 py-1.5 text-ems-text-primary">{formatDay(r.day)}</td>
              <td className="px-3 py-1.5 text-right text-ems-text-primary">{formatNumber(r.daily_peak_kw)}</td>
              <td className="px-3 py-1.5 text-right text-ems-text-primary">{formatNumber(r.daily_kwh)}</td>
              <td className="px-3 py-1.5 text-right text-ems-text-primary">{formatNumber(r.daily_fuel_mmbtu)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </PreviewFrame>
  );
}

function MonthlyPreview({ result }: { result: ReportResult<MonthlyReportRow> }) {
  return (
    <PreviewFrame metadata={result.metadata}>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-ems-text-muted">
          <tr>
            <th className="text-left px-3 py-2">Month</th>
            <th className="text-right px-3 py-2">Monthly Peak kW</th>
            <th className="text-right px-3 py-2">Monthly kWh</th>
            <th className="text-right px-3 py-2">Monthly MMBtu</th>
          </tr>
        </thead>
        <tbody>
          {result.rows.map(r => (
            <tr key={r.month} className="border-t border-ems-border">
              <td className="px-3 py-1.5 text-ems-text-primary">{formatMonth(r.month)}</td>
              <td className="px-3 py-1.5 text-right text-ems-text-primary">{formatNumber(r.monthly_peak_kw)}</td>
              <td className="px-3 py-1.5 text-right text-ems-text-primary">{formatNumber(r.monthly_kwh)}</td>
              <td className="px-3 py-1.5 text-right text-ems-text-primary">{formatNumber(r.monthly_fuel_mmbtu)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </PreviewFrame>
  );
}

function PreviewFrame({
  metadata,
  children,
}: {
  metadata: {
    power_metric: string;
    row_count: number;
    query_time_ms: number;
    integral_formula: string;
  };
  children: React.ReactNode;
}) {
  return (
    <div className="border border-ems-border rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-ems-bg text-xs text-ems-text-muted border-b border-ems-border">
        <span>
          Power metric: <span className="text-ems-text-primary">{metadata.power_metric}</span> ·{' '}
          {metadata.row_count} row{metadata.row_count === 1 ? '' : 's'} · {metadata.query_time_ms} ms
        </span>
        <span className="italic">{metadata.integral_formula}</span>
      </div>
      <div className="max-h-96 overflow-auto">{children}</div>
    </div>
  );
}
